import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageHeader, SectionHeader } from "../components/ui-blocks";
import { CONTACT_EMAIL, IMAGES } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const preset = params.get("type");

  const offices = t("data.offices", { returnObjects: true });
  const engagementList = t("contact.engagementList", { returnObjects: true });

  const INQUIRY_OPTIONS = [
    { value: "investor", label: t("contact.inquiryOptions.investor") },
    { value: "strategic", label: t("contact.inquiryOptions.strategic") },
    { value: "institutional", label: t("contact.inquiryOptions.institutional") },
    { value: "general", label: t("contact.inquiryOptions.general") },
  ];

  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    phone: "",
    inquiry_type: "general",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (preset && INQUIRY_OPTIONS.some((o) => o.value === preset)) {
      setForm((f) => ({ ...f, inquiry_type: preset }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.toast.missing"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.toast.success"));
      setForm({
        name: "",
        email: "",
        organization: "",
        phone: "",
        inquiry_type: "general",
        subject: "",
        message: "",
      });
    } catch (err) {
      toast.error(t("contact.toast.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-300 focus:border-[#0B1A3D] rounded-full text-[#0B1A3D] placeholder:text-slate-400 px-5 py-3.5 outline-none transition-colors";
  const textareaCls =
    "w-full bg-white border border-slate-300 focus:border-[#0B1A3D] rounded-2xl text-[#0B1A3D] placeholder:text-slate-400 px-5 py-4 outline-none transition-colors";

  return (
    <>
      <PageHeader
        testId="contact-header"
        overline={t("contact.headerOverline")}
        title={t("contact.headerTitle")}
        lead={t("contact.headerLead")}
        backgroundImage={IMAGES.sandton}
      />

      <section className="section-pad" data-testid="contact-main">
        <div className="container-mtbl grid lg:grid-cols-12 gap-14">
          <aside className="lg:col-span-4 space-y-10">
            <div>
              <p className="overline mb-4">{t("contact.directOverline")}</p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-link"
                className="inline-flex items-center gap-3 text-[#0B1A3D] hover:text-[#13275B]"
              >
                <Mail size={18} />
                <span className="text-lg font-semibold">{CONTACT_EMAIL}</span>
              </a>
            </div>

            <div>
              <p className="overline mb-5">{t("contact.officesOverline")}</p>
              <div className="space-y-6">
                {offices.map((o) => (
                  <div
                    key={o.city}
                    data-testid={`contact-office-${o.city
                      .split(",")[0]
                      .toLowerCase()
                      .replace(/[^a-z]/g, "")}`}
                    className="border border-slate-200 rounded-2xl overflow-hidden bg-white"
                  >
                    {o.image && IMAGES[o.image] && (
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                          src={IMAGES[o.image]}
                          alt={o.city}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex gap-3 text-slate-600 leading-relaxed p-5">
                      <MapPin size={18} className="text-slate-400 mt-1 shrink-0" />
                      <div>
                        <p className="text-[#0B1A3D] font-semibold">{o.city}</p>
                        {o.address && (
                          <p className="text-slate-600 text-sm mt-1">
                            {o.address}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="overline mb-4">{t("contact.engagementOverline")}</p>
              <ul className="space-y-2 text-slate-700 text-sm">
                {engagementList.map((item) => (
                  <li key={item}>· {item}</li>
                ))}
              </ul>
            </div>
          </aside>

          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-8 space-y-5 border border-slate-200 rounded-2xl shadow-[0_10px_30px_rgba(11,26,61,0.06)] p-8 md:p-12 bg-white"
          >
            <SectionHeader
              overline={t("contact.formOverline")}
              title={t("contact.formTitle")}
            />

            <div className="grid md:grid-cols-2 gap-5 pt-4">
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t("contact.form.fullName")}
                data-testid="contact-name"
                className={inputCls}
                required
              />
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder={t("contact.form.email")}
                data-testid="contact-email"
                className={inputCls}
                required
              />
              <input
                name="organization"
                value={form.organization}
                onChange={handleChange}
                placeholder={t("contact.form.organization")}
                data-testid="contact-organization"
                className={inputCls}
              />
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder={t("contact.form.phone")}
                data-testid="contact-phone"
                className={inputCls}
              />
              <select
                name="inquiry_type"
                value={form.inquiry_type}
                onChange={handleChange}
                data-testid="contact-inquiry-type"
                className={inputCls}
              >
                {INQUIRY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder={t("contact.form.subject")}
                data-testid="contact-subject"
                className={inputCls}
              />
            </div>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={6}
              placeholder={t("contact.form.message")}
              data-testid="contact-message"
              className={textareaCls}
              required
            />

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="btn-primary w-full md:w-auto disabled:opacity-60"
            >
              {submitting ? t("contact.form.submitting") : t("contact.form.submit")}
            </button>

            <p className="text-xs text-slate-500 leading-relaxed pt-2">
              {t("contact.form.consent")}
            </p>
          </form>
        </div>
      </section>
    </>
  );
}
